import { AmountDisplay, amountString } from "@/app/shared/ui/amount-display";
import { statusColors } from "@/lib/design-tokens";
import { cn } from "@/lib/utils";
import { useDemoModeStore } from "@/store/demoMode";
import type { ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import type { CreditCardOverview } from "@/types";

interface PeriodSummaryHeaderProps {
  overview: CreditCardOverview | undefined;
  isLoading: boolean;
  month: string;
}

function SummaryTile({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  tone?: "default" | "success" | "warning" | "destructive";
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1.5 text-xl font-medium tabular-nums",
          tone === "success" && statusColors.success.text,
          tone === "warning" && statusColors.warning.text,
          tone === "destructive" && statusColors.error.text,
        )}
      >
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function PeriodSummaryHeader({ overview, isLoading }: PeriodSummaryHeaderProps) {
  const { enabled: isDemoMode } = useDemoModeStore();
  if (isLoading && !overview) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          <div key={i} className="h-[88px] animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }
  if (!overview) return null;

  const { totals, spendDeltaVsPrevious, cards } = overview;
  const delta = spendDeltaVsPrevious;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <SummaryTile
          label="Total Spent"
          value={<AmountDisplay amount={totals.totalSpend} />}
          sub={
            cards.length > 0
              ? `${cards.length} card${cards.length === 1 ? "" : "s"} · ${cards.reduce((s, c) => s + c.transactionCount, 0)} txns`
              : undefined
          }
        />
        <SummaryTile
          label="Total Bills Due"
          value={<AmountDisplay amount={totals.totalApproxOrBillAmount} />}
          sub={`incl. approx · ${amountString(totals.totalPaid, "INR", isDemoMode)} paid`}
        />
        <SummaryTile
          label="Outstanding"
          value={<AmountDisplay amount={totals.totalOutstanding} />}
          tone={totals.totalOutstanding > 0 ? "warning" : "success"}
          sub={
            totals.totalRepayments > 0
              ? `${amountString(totals.totalRepayments, "INR", isDemoMode)} repaid from own accounts`
              : undefined
          }
        />
      </div>

      {/* Previous-period delta strip */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>vs previous billing period:</span>
        {delta == null ? (
          <span className="inline-flex items-center gap-1">
            <Minus className="size-3.5" /> no data
          </span>
        ) : (
          <span
            className={cn(
              "inline-flex items-center gap-1 font-medium",
              delta > 0 ? "text-destructive" : delta < 0 ? "text-success" : "",
            )}
            title={`Previous period spend: ${amountString(overview.previousMonthTotalSpend, "INR", isDemoMode)}`}
          >
            {delta > 0 ? (
              <ArrowUpRight className="size-3.5" />
            ) : delta < 0 ? (
              <ArrowDownRight className="size-3.5" />
            ) : (
              <Minus className="size-3.5" />
            )}
            {Math.abs(delta)}% {delta > 0 ? "more" : delta < 0 ? "less" : "same"}
            <span className="font-normal text-muted-foreground/70">
              (was <AmountDisplay amount={overview.previousMonthTotalSpend} />)
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
